import type { ReactElement } from 'react';
import { useEffect } from 'react';
import styled from 'styled-components';
import { Alert, Modal, Tag, useAlert } from '../../../components';
import { Device } from '../../../model';
import { useRemoveEncoder } from '../graphql';

interface Props {
  show: boolean;
  onClose: () => void;
  selectedDevice: Device;
  handleDevicesRefetch: () => void;
}

const Intro = styled.p`
  margin: 2px 2px 4px;
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.55;
  color: rgba(176, 186, 209, 0.72);
`;

const Card = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 11px;
  background: rgba(8, 14, 30, 0.35);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 11px;
`;

const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;

  .cap {
    font-weight: 600;
    font-size: 9px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.5);
    flex-shrink: 0;
  }

  .val {
    text-align: right;
    color: rgb(240, 242, 245);
    font-weight: 600;
    font-size: 13px;
    overflow-wrap: anywhere;
  }

  .sub {
    display: block;
    margin-top: 3px;
    font-weight: 500;
    font-size: 11px;
    color: rgba(176, 186, 209, 0.55);
  }
`;

const Button = styled.button<{ $variant: 'cancel' | 'danger' }>`
  border-radius: 9px;
  font-weight: 600;
  font-size: 13px;
  padding: 9px 18px;
  cursor: pointer;
  transition: all var(--transition);

  ${({ $variant }): string =>
    $variant === 'cancel'
      ? `
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.12);
        color: rgba(213, 221, 238, 0.92);
      `
      : `
        background: rgba(255, 80, 95, 0.16);
        border: 1px solid rgba(255, 80, 95, 0.45);
        color: rgb(255, 138, 147);
      `}

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  &:hover:not(:disabled),
  &:focus {
    outline: none;
    filter: brightness(1.1);
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

// "Online since" / "Last online" + the last status-change timestamp.
const lastSeen = (device: Device): string => {
  const online = Device.Status.online === device.status;

  const date = device.statusUpdatedAt
    .toLocaleDateString('en', { day: '2-digit', year: '2-digit', month: '2-digit' })
    .replaceAll('/', '-');

  const time = device.statusUpdatedAt.toLocaleTimeString('en', {
    hour12: true,
    hour: '2-digit',
    minute: '2-digit'
  });

  return `${online ? 'Online since' : 'Last online'} ${date} at ${time}`;
};

export const UnlinkModal = ({
  show,
  onClose,
  selectedDevice,
  handleDevicesRefetch
}: Props): ReactElement => {
  const [removeEncoder, { loading, error }] = useRemoveEncoder();
  const alert = useAlert();

  const online = Device.Status.online === selectedDevice.status;

  useEffect(() => {
    if (error) {
      alert.show('Failed to unlink the encoder. Please try again.');
    }
  }, [error, alert]);

  const handleRemove = (): void => {
    void (async (): Promise<void> => {
      const success = await removeEncoder({ location: selectedDevice.id });

      if (success) {
        handleDevicesRefetch();
        onClose();
      }
    })();
  };

  return (
    <Modal
      open={show}
      onClose={onClose}
      title='Unlink Encoder'
      width={460}
      footer={
        <Actions>
          <Button type='button' $variant='cancel' onClick={onClose}>
            Cancel
          </Button>
          <Button type='button' $variant='danger' disabled={loading} onClick={handleRemove}>
            {loading ? 'Unlinking…' : 'Unlink'}
          </Button>
        </Actions>
      }
    >
      <Intro>Unlink this encoder from its location?</Intro>

      <Card>
        <Row>
          <span className='cap'>Status</span>
          <span>
            <Tag size='small' className={online ? 'cc-green' : 'cc-red'} icon='fas fa-circle' iconSize={7}>
              {online ? 'Online' : 'Offline'}
            </Tag>
            <span className='sub'>{lastSeen(selectedDevice)}</span>
          </span>
        </Row>
        <Row>
          <span className='cap'>CID</span>
          <span className='val'>{selectedDevice.cid}</span>
        </Row>
        <Row>
          <span className='cap'>Org / Location</span>
          <span className='val'>
            {selectedDevice.organization}
            <span className='sub'>{selectedDevice.location}</span>
          </span>
        </Row>
      </Card>

      {alert.message !== undefined && <Alert message={alert.message} onDismiss={alert.clear} />}
    </Modal>
  );
};
